import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { Sucursal } from "@/services/accounts.service";
import { useRoles } from "@/hooks/useRoles";
import { Flex } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  fertSlotRequestOrderCreationSchema,
  FertSlotRequestOrderCreationForm,
} from "./schema/fert-slot-request-order-creation.schema";
import { OrderCard } from "./OrderCard";
import {
  buildDispatchPlaceOptions,
  buildProductOptions,
  createOrdersHandler,
  fetchBranchesHandler,
  fetchProductsHandler,
  handleCancelHandler,
  handleDeleteOrderHandler,
  handleSaveAndCreateAnotherOrderHandler,
  preloadProductsFromDetail,
} from "./schema/handler-functions";
import { fetchDetailHandler } from "../detail/schema/handler-functions";
import { FertSlotRequestDetail } from "../detail/schema/fert-slot-request-detail.schema";



export const FertSlotRequestOrderCreationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number>(0);

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingCreateOrders, setLoadingCreateOrders] = useState(false);

  const [fertilizantes, setFertilizantes] = useState<
    { id: number; nombre: string }[]
  >([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FertSlotRequestOrderCreationForm>({
    resolver: zodResolver(fertSlotRequestOrderCreationSchema),
    defaultValues: {
      orders: [
        {
          dispatchPlace: "",
          observations: "",
          products: [{ productId: "", tons: "" }],
        },
      ],
    },
  });

  const watched = useWatch({ control });

  const {
    fields: orderFields,
    append: appendOrder,
    remove: removeOrder,
    replace: replaceOrders,
  } = useFieldArray({
    control,
    name: "orders",
  });

  const hasNoFilledValues = useMemo(() => {
    const orders = watched?.orders ?? [];
    if (!orders.length) return true;

    const normalizeSingle = (v: any) => {
      if (Array.isArray(v)) return v?.[0];
      return v;
    };

    return orders.some((o) => {
      const products = o?.products ?? [];
      const hasInvalidProducts =
        !products.length ||
        products.some((p) => {
          const productId = normalizeSingle(p?.productId);
          return !productId || !p?.tons || Number(p?.tons) <= 0;
        });

      const dispatchPlace = normalizeSingle(o?.dispatchPlace);
      return !dispatchPlace || hasInvalidProducts;
    });
  }, [watched]);

  const handleCancel = () => {
    handleCancelHandler(navigate, id);
  };

  const onSubmit = async (_values: FertSlotRequestOrderCreationForm) => {
    setLoadingCreateOrders(true);
    try {
      await createOrdersHandler({
        id,
        values: _values,
        navigate,
        fetchPermissions,
      });
    } finally {
      setLoadingCreateOrders(false);
    }
  };

  const fetchProducts = async () => {
    await fetchProductsHandler({
      navigate,
      fetchPermissions,
      setLoadingProducts,
      setFertilizantes,
    });
  };

  const fetchBranches = async () => {
    await fetchBranchesHandler({
      navigate,
      fetchPermissions,
      setLoadingBranches,
      setSucursales,
    });
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (!isActive) return;

    fetchProducts();
    fetchBranches();
  }, [isActive]);

  const [detail, setDetail] = useState<FertSlotRequestDetail | null>(null);
  const didPreloadFromDetail = useRef(false);

  useEffect(() => {
    if (!isActive || !id) return;

    fetchDetailHandler({
      id,
      navigate,
      fetchPermissions,
      setLoadingDetail: () => {},
      setDetail,
    });
  }, [isActive, id, navigate, fetchPermissions]);

  useEffect(() => {
    preloadProductsFromDetail({
      detail,
      didPreloadFromDetail,
      fertilizantes,
      replaceOrders,
    });
  }, [detail, fertilizantes, replaceOrders]);

  const productOptions = useMemo(() => {
    const requestProducts = (detail?.productos ?? [])
      .map((p: any) => {
        const id = Number(p?.idProducto ?? p?.id ?? 0);
        const nombre = String(p?.nombreProducto ?? p?.nombre ?? "");
        const activo = typeof p?.activo === "boolean" ? p.activo : true;
        return { id, nombre, activo };
      })
      .filter((p: any) => Number.isFinite(p.id) && p.nombre.trim().length);

    const requestById = new Map<number, { id: number; nombre: string; activo: boolean }>();
    for (const p of requestProducts) {
      if (!requestById.has(p.id)) requestById.set(p.id, p);
    }

    const requestInactiveIds = new Set(
      [...requestById.values()].filter((p) => !p.activo).map((p) => p.id),
    );

    const activeCatalogOptions = buildProductOptions(fertilizantes).filter(
      (o: any) => !requestInactiveIds.has(Number(o?.value ?? 0)),
    );

    const activeIds = new Set(
      activeCatalogOptions.map((o: any) => Number(o?.value ?? 0)).filter(Number.isFinite),
    );

    const extrasActive: any[] = [];
    const extrasInactive: any[] = [];

    for (const p of requestById.values()) {
      if (activeIds.has(p.id)) continue;
      if (p.activo) {
        extrasActive.push({
          label: p.nombre,
          value: String(p.id),
          isDisabled: false,
        });
      } else {
        extrasInactive.push({
          label: `${p.nombre} (Inactivo)`,
          value: String(p.id),
          isDisabled: true,
        });
      }
    }

    return [...activeCatalogOptions, ...extrasActive, ...extrasInactive];
  }, [fertilizantes, detail?.productos]);

  const inactiveProductIds = useMemo(() => {
    return new Set(
      (productOptions ?? [])
        .filter((o: any) => Boolean(o?.isDisabled))
        .map((o: any) => String(o?.value ?? "")),
    );
  }, [productOptions]);

  const hasInactiveSelectedProduct = useMemo(() => {
    const orders = watched?.orders ?? [];
    return orders.some((o: any) =>
      (o?.products ?? []).some((p: any) => {
        const raw = Array.isArray(p?.productId) ? p.productId?.[0] : p?.productId;
        const v = raw !== undefined && raw !== null ? String(raw) : "";
        return v && inactiveProductIds.has(v);
      }),
    );
  }, [watched?.orders, inactiveProductIds]);

  const dispatchPlaceOptions = useMemo(
    () => buildDispatchPlaceOptions(sucursales),
    [sucursales],
  );

  const handleDeleteOrder = (index: number) => {
    handleDeleteOrderHandler({
      index,
      removeOrder,
      setEditingIndex,
    });
  };

  const handleSaveAndCreateAnotherOrder = () => {
    handleSaveAndCreateAnotherOrderHandler({
      appendOrder,
      setEditingIndex,
      ordersLength: orderFields.length,
    });
  };

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Realizar pedido"
        addButtonLabel="Continuar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loadingCreateOrders || hasInactiveSelectedProduct}
        loading={loadingCreateOrders}
      />

      <Flex flexDir="column" gap="16px" marginTop="30px">
        {orderFields.map((order, orderIndex) => (
          <OrderCard
            key={order.id}
            control={control as any}
            errors={errors as any}
            register={register as any}
            orderIndex={orderIndex}
            isEditing={editingIndex === orderIndex}
            setEditingIndex={setEditingIndex}
            onDeleteOrder={handleDeleteOrder}
            onSaveAndCreateAnotherOrder={handleSaveAndCreateAnotherOrder}
            disableSaveAndCreateAnotherOrder={hasInactiveSelectedProduct}
            productOptions={productOptions}
            dispatchPlaceOptions={dispatchPlaceOptions}
            loadingProducts={loadingProducts}
            loadingBranches={loadingBranches}
            orderFieldsLength={orderFields.length}
            watchedOrders={watched?.orders ?? []}
          />
        ))}
      </Flex>
    </Flex>
  );
};
